import React, { useEffect, useState } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { api } from '@/lib/api';
import { useSettings } from '@/contexts/SettingsContext';
import { TID } from '@/lib/testIds';
import { toast } from 'sonner';
import { Plus, X, ChevronDown } from 'lucide-react';

const DAY_LABEL = { mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday', sun: 'Sunday' };
const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const Section = ({ title, description, defaultOpen = true, children, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="sb-card" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-4 sm:p-5 text-left"
        aria-expanded={open}
      >
        <div>
          <h2 className="font-display text-lg sm:text-xl text-white">{title}</h2>
          {description && <p className="text-xs text-white/55 mt-1">{description}</p>}
        </div>
        <ChevronDown
          size={18}
          className={`text-white/60 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && <div className="px-4 sm:px-5 pb-5 pt-1 border-t border-[hsl(var(--sb-border))]">{children}</div>}
    </section>
  );
};

export default function AdminSettingsPage() {
  const { settings, refresh } = useSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newClosedDate, setNewClosedDate] = useState('');

  useEffect(() => { if (settings) setForm({ ...settings }); }, [settings]);

  if (!form) return <AdminLayout><div className="text-white">Loading…</div></AdminLayout>;

  const setHourField = (day, key, value) => {
    const arr = (form.opening_hours || []).map((h) => (h.day === day ? { ...h, [key]: value } : h));
    setForm({ ...form, opening_hours: arr });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings', {
        restaurant_name: form.restaurant_name,
        tagline: form.tagline,
        address: form.address,
        phone: form.phone,
        email: form.email,
        logo_url: form.logo_url,
        opening_hours: form.opening_hours,
        capacity_per_slot: parseInt(form.capacity_per_slot, 10) || 0,
        max_party_size: parseInt(form.max_party_size, 10) || 0,
        booking_slot_interval_minutes: parseInt(form.booking_slot_interval_minutes, 10) || 30,
        reservation_lead_minutes: parseInt(form.reservation_lead_minutes, 10) || 0,
        closed_dates: form.closed_dates,
        social_links: form.social_links,
        whatsapp_number: form.whatsapp_number || '',
        whatsapp_message: form.whatsapp_message || '',
        // NOTE: map_embed_url deliberately not exposed in the admin UI — derived from address.
      });
      toast.success('Settings saved');
      await refresh();
    } catch (e) { toast.error('Save failed'); } finally { setSaving(false); }
  };

  const addClosedDate = () => {
    if (!newClosedDate) return;
    setForm({
      ...form,
      closed_dates: [...(form.closed_dates || []), newClosedDate].filter((v, i, a) => a.indexOf(v) === i),
    });
    setNewClosedDate('');
  };
  const removeClosedDate = (d) =>
    setForm({ ...form, closed_dates: (form.closed_dates || []).filter((x) => x !== d) });

  return (
    <AdminLayout>
      <div data-testid={TID.admin.settingsPage} className="space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl text-white">SETTINGS</h1>
          <button
            onClick={save}
            disabled={saving}
            data-testid="settings-save-button"
            className="h-10 px-5 rounded-md bg-[hsl(var(--sb-gold))] text-black font-semibold disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>

        {/* A. Restaurant details */}
        <Section
          testId="settings-section-restaurant"
          title="Restaurant details"
          description="Name, tagline, contact info and logo shown across the customer site"
        >
          <div className="grid sm:grid-cols-2 gap-3 pt-4">
            <F label="Restaurant name" value={form.restaurant_name} onChange={(v) => setForm({ ...form, restaurant_name: v })} />
            <F label="Tagline" value={form.tagline} onChange={(v) => setForm({ ...form, tagline: v })} />
            <F label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} className="sm:col-span-2" />
            <F label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
            <F label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            <F label="Logo URL" value={form.logo_url} onChange={(v) => setForm({ ...form, logo_url: v })} className="sm:col-span-2" />
          </div>
        </Section>

        {/* B. Opening hours — collapsed by default */}
        <Section
          testId="settings-section-hours"
          title="Opening hours"
          description="Set open/closed and hours for each weekday"
          defaultOpen={false}
        >
          <div className="space-y-2 pt-4">
            {DAYS.map((day) => {
              const h = (form.opening_hours || []).find((x) => x.day === day) || { day, closed: true };
              return (
                <div key={day} className="grid grid-cols-12 items-center gap-3">
                  <div className="col-span-4 sm:col-span-3 text-white">{DAY_LABEL[day]}</div>
                  <label className="col-span-3 sm:col-span-2 inline-flex items-center gap-2 text-sm text-white/85">
                    <input
                      type="checkbox"
                      checked={!h.closed}
                      onChange={(e) => setHourField(day, 'closed', !e.target.checked)}
                    />
                    Open
                  </label>
                  <div className="col-span-5 sm:col-span-3">
                    <input
                      type="time"
                      disabled={h.closed}
                      value={h.open || ''}
                      onChange={(e) => setHourField(day, 'open', e.target.value)}
                      className="w-full h-10 px-3 rounded-md bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] text-white disabled:opacity-40"
                    />
                  </div>
                  <div className="col-span-12 sm:col-span-4 sm:col-start-9">
                    <input
                      type="time"
                      disabled={h.closed}
                      value={h.close || ''}
                      onChange={(e) => setHourField(day, 'close', e.target.value)}
                      className="w-full h-10 px-3 rounded-md bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] text-white disabled:opacity-40"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </Section>

        {/* C. Bookings — grouped booking configuration */}
        <Section
          testId="settings-section-bookings"
          title="Bookings"
          description="Table reservation configuration"
        >
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-4">
            <F
              label="Capacity per slot"
              type="number"
              value={form.capacity_per_slot}
              onChange={(v) => setForm({ ...form, capacity_per_slot: v })}
            />
            <F
              label="Max online party size"
              type="number"
              value={form.max_party_size}
              onChange={(v) => setForm({ ...form, max_party_size: v })}
              hint="Larger groups are directed to WhatsApp"
              testid="settings-max-party-size"
            />
            <F
              label="Booking slot interval (minutes)"
              type="number"
              value={form.booking_slot_interval_minutes ?? 30}
              onChange={(v) => setForm({ ...form, booking_slot_interval_minutes: v })}
              hint="e.g. 30 for half-hourly, 15 for quarter-hourly"
              testid="settings-slot-interval"
            />
            <F
              label="Reservation lead time (minutes)"
              type="number"
              value={form.reservation_lead_minutes}
              onChange={(v) => setForm({ ...form, reservation_lead_minutes: v })}
            />
          </div>

          <div className="mt-5 pt-4 border-t border-[hsl(var(--sb-border))]">
            <div className="text-[11px] uppercase tracking-widest text-white/55 mb-2">Closed dates</div>
            <div className="flex items-center gap-2 mb-3">
              <input
                type="date"
                value={newClosedDate}
                onChange={(e) => setNewClosedDate(e.target.value)}
                className="h-10 px-3 rounded-md bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] text-white"
              />
              <button
                onClick={addClosedDate}
                className="h-10 px-3 rounded-md bg-[hsl(var(--sb-gold))] text-black font-semibold inline-flex items-center gap-1"
              >
                <Plus size={14} /> Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {(form.closed_dates || []).map((d) => (
                <span
                  key={d}
                  className="inline-flex items-center gap-2 h-8 px-3 rounded-full bg-[hsl(var(--sb-red))]/15 text-[hsl(var(--sb-red))] border border-[hsl(var(--sb-red))]/30 text-sm"
                >
                  {d}
                  <button onClick={() => removeClosedDate(d)} className="text-[hsl(var(--sb-red))]">
                    <X size={12} />
                  </button>
                </span>
              ))}
              {!(form.closed_dates || []).length && (
                <span className="text-white/55 text-sm">No closed dates configured.</span>
              )}
            </div>
          </div>
        </Section>

        {/* D. Delivery & social */}
        <Section
          testId="settings-section-delivery-social"
          title="Delivery & social"
          description="Blank delivery URLs will hide the corresponding public CTA"
        >
          <div className="pt-4">
            <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--sb-gold))] mb-2">Delivery platforms</div>
            <div className="grid sm:grid-cols-3 gap-3">
              <F label="Uber Eats URL" value={form.social_links?.uber_eats}
                 onChange={(v) => setForm({ ...form, social_links: { ...form.social_links, uber_eats: v } })} />
              <F label="Deliveroo URL" value={form.social_links?.deliveroo}
                 onChange={(v) => setForm({ ...form, social_links: { ...form.social_links, deliveroo: v } })} />
              <F label="Just Eat URL" value={form.social_links?.just_eat}
                 onChange={(v) => setForm({ ...form, social_links: { ...form.social_links, just_eat: v } })} />
            </div>
            <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--sb-gold))] mt-5 mb-2">Social</div>
            <div className="grid sm:grid-cols-3 gap-3">
              <F label="Instagram" value={form.social_links?.instagram}
                 onChange={(v) => setForm({ ...form, social_links: { ...form.social_links, instagram: v } })} />
              <F label="Facebook" value={form.social_links?.facebook}
                 onChange={(v) => setForm({ ...form, social_links: { ...form.social_links, facebook: v } })} />
              <F label="TikTok" value={form.social_links?.tiktok}
                 onChange={(v) => setForm({ ...form, social_links: { ...form.social_links, tiktok: v } })} />
            </div>
          </div>
        </Section>

        {/* E. WhatsApp */}
        <Section
          testId="settings-section-whatsapp"
          title="WhatsApp"
          description="Powers the floating WhatsApp button and Larger group booking link"
        >
          <div className="grid sm:grid-cols-2 gap-3 pt-4">
            <F
              label="WhatsApp number"
              value={form.whatsapp_number}
              onChange={(v) => setForm({ ...form, whatsapp_number: v })}
              hint="International format, digits only — e.g. 447951482290"
              testid="settings-whatsapp-number"
            />
            <F
              label="Pre-filled message"
              value={form.whatsapp_message}
              onChange={(v) => setForm({ ...form, whatsapp_message: v })}
            />
          </div>
        </Section>
      </div>
    </AdminLayout>
  );
}

const F = ({ label, value, onChange, type = 'text', className = '', hint, testid }) => (
  <label className={`block ${className}`}>
    <span className="text-[11px] uppercase tracking-widest text-white/55">{label}</span>
    <input
      type={type}
      value={value === null || value === undefined ? '' : value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="mt-1 w-full h-11 px-3 rounded-md bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] text-white"
    />
    {hint && <span className="text-[10px] text-white/45 mt-1 block">{hint}</span>}
  </label>
);
